import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from "../styles/Gallery.module.css";

interface GalleryImage {
  id: number;
  signedUrl: string;
  productId: number;
  productName: string;
  categoryName: string;
  inStock: boolean;
}

interface Category {
  id: number;
  name: string;
}

interface Pagination {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

const Gallery: React.FC = () => {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<number>(0);
  const [pagination, setPagination] = useState<Pagination>({
    total: 0,
    page: 1,
    limit: 15,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);

  // ✅ Fetch categories
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/gallery`, {
          params: { page: 1, limit: 1 },
        });

        // Normalize category data to always be { id, name }
        const apiCategories = res.data.categories || [];
        const normalized = apiCategories.map((cat: any) => ({
          id: cat.id ?? 0,
          name: cat.name ?? String(cat),
        }));

        setCategories([{ id: 0, name: "All" }, ...normalized]);
      } catch (err) {
        console.error("Failed to fetch categories", err);
        setCategories([{ id: 0, name: "All" }]);
      }
    };

    fetchCategories();
  }, []);

  // ✅ Fetch gallery
  const fetchGallery = async (page = 1, categoryId?: number) => {
    setLoading(true);
    try {
      const params: any = { page, limit: pagination.limit };
      if (categoryId && categoryId !== 0) params.categoryId = categoryId;

      const res = await axios.get(`${process.env.REACT_APP_BASE_URL}/gallery`, { params });
      setImages(res.data.data);
      setPagination(res.data.pagination);
    } catch (error) {
      console.error("Failed to load gallery", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGallery(1, selectedCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory]);

  const handleFilter = (categoryId: number) => {
    setSelectedCategory(categoryId);
    fetchGallery(1, categoryId);
  };

  const handlePageChange = (newPage: number) => {
    fetchGallery(newPage, selectedCategory);
  };

  if (loading)
    return (
      <div className="loading">
        <div className="gear" />
        <p>Loading Gallery...</p>
      </div>
    );

  return (
    <div className={styles.galleryContainer}>
      <h1>Product Gallery</h1>

      {/* ✅ Category Filter */}
      <div className={styles.filterBar}>
        <label htmlFor="category">Filter by category:</label>
        <select
          id="category"
          value={selectedCategory}
          onChange={(e) => handleFilter(Number(e.target.value))}
        >
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.name}
            </option>
          ))}
        </select>
      </div>

      {/* ✅ Gallery Grid */}
      <div className={styles.grid}>
        {images.map((img) => (
          <div
            key={img.id}
            className={styles.card}
            onClick={() => setSelectedImage(img)}
          >
            <div className={styles.imageWrapper}>
              {img.signedUrl ? (
                <img
                  src={img.signedUrl}
                  alt={img.productName}
                  className={styles.image}
                />
              ) : (
                <div className={styles.imagePlaceholder}>
                  <span>No Image</span>
                </div>
              )}
              <span
                className={`${styles.stockBadge} ${
                  img.inStock ? styles.inStock : styles.outOfStock
                }`}
              >
                {img.inStock ? "In Stock" : "Out of Stock"}
              </span>
            </div>
            <div className={styles.info}>
              <h3>{img.productName}</h3>
              <p>{img.categoryName}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ✅ Pagination */}
      <div className={styles.pagination}>
        {Array.from({ length: pagination.totalPages }, (_, i) => (
          <button
            key={i}
            className={`${styles.paginationButton} ${
              i + 1 === pagination.page ? styles.paginationButtonActive : ""
            }`}
            onClick={() => {
              handlePageChange(i + 1); // existing logic
              window.scrollTo({ top: 0, behavior: "smooth" }); // 👈 scrolls to top
            }}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {/* ✅ Modal */}
      {selectedImage && (
        <div
          className={styles.modalOverlay}
          onClick={() => setSelectedImage(null)}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className={styles.closeButton}
              onClick={() => setSelectedImage(null)}
            >
              ✕
            </button>
            <img
              src={selectedImage.signedUrl}
              alt={selectedImage.productName}
              className={styles.modalImage}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
